import { useEffect, useMemo, useState } from 'react'

// Cadence mirrors ai-flow-spa's AiTextAreaAnimatedPlaceholder.
const TYPING_INTERVAL_MS = 20
const DELETING_INTERVAL_MS = 5
const FULL_TEXT_PAUSE_MS = 1500

type TAnimationPhase = 'typing' | 'waiting' | 'deleting'

/**
 * Typewriter loop over a list of placeholder texts: type a sentence, pause
 * with a blinking caret, delete it, move to the next. The '' entries
 * interleaved between sentences reproduce the reference pacing — an empty
 * target skips straight to the pause phase, giving a caret-only gap
 * between sentences.
 */
export const useAnimatedPlaceholder = (texts: string[]) => {
  const pacedTexts = useMemo(() => texts.flatMap(text => ['', text]), [texts])

  const [textIndex, setTextIndex] = useState(0)
  const [animatedText, setAnimatedText] = useState('')
  const [phase, setPhase] = useState<TAnimationPhase>('typing')

  const targetText = pacedTexts[textIndex % pacedTexts.length] ?? ''

  useEffect(() => {
    if (phase === 'typing') {
      // Also self-heals when texts change mid-animation (language switch):
      // an overshooting animatedText falls through to waiting -> deleting.
      if (animatedText.length >= targetText.length) {
        setPhase('waiting')
        return
      }
      const timeoutId = window.setTimeout(
        () => setAnimatedText(targetText.slice(0, animatedText.length + 1)),
        TYPING_INTERVAL_MS
      )
      return () => window.clearTimeout(timeoutId)
    }

    if (phase === 'waiting') {
      const timeoutId = window.setTimeout(
        () => setPhase('deleting'),
        FULL_TEXT_PAUSE_MS
      )
      return () => window.clearTimeout(timeoutId)
    }

    if (!animatedText.length) {
      setTextIndex(prev => (prev + 1) % pacedTexts.length)
      setPhase('typing')
      return
    }
    const timeoutId = window.setTimeout(
      () => setAnimatedText(prev => prev.slice(0, -1)),
      DELETING_INTERVAL_MS
    )
    return () => window.clearTimeout(timeoutId)
  }, [phase, animatedText, targetText, pacedTexts.length])

  return {
    animatedText,
    // The caret blinks while resting on a full sentence (and in the empty
    // gaps); it is solid while typing/deleting.
    isCaretBlinking: phase === 'waiting'
  }
}
